import { useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, MenuItem, Stack, TextField, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";

const rowKey = item => item.template_id ? `t${item.template_id}` : `e${item.source_eval_id}`;
const limit = (value, n) => Array.from(value).slice(0,n).join("");
const riskText = row => [row.hazard, row.improve_plan].filter(Boolean).join(" : ");
function ActionIcon({ label, children, ...props }) {
  return <Tooltip title={label}><IconButton size="small" aria-label={label} {...props}>{children}</IconButton></Tooltip>;
}

export function DefaultChecklistDialog({ templates, scope, date, api, onSaved, onClose }) {
  const [importing,setImporting] = useState(false);
  const [base] = useState(() => templates.map(t => Number(t.id)));
  const [items,setItems] = useState(() => templates.map(t => ({ id:Number(t.id), key:'t'+t.id, title:t.title })));
  const serial = useRef(0);
  const mutation = useMutation({
    mutationFn: () => api.put("/tbm/checklist-templates", { base_ids:base, items:items.map(({id,title}) => ({id,title:title.trim()})) }),
    onSuccess: ({data}) => onSaved(data),
  });
  const titles = items.map(item => item.title.trim());
  const duplicates = new Set(titles).size !== titles.length;
  const dismiss = () => { if (!mutation.isPending) onClose(); };
  return <Dialog open onClose={dismiss} maxWidth="sm" fullWidth aria-labelledby="tbm-default-title">
    <DialogTitle id="tbm-default-title">기본 체크리스트 편집</DialogTitle>
    <DialogContent dividers>
      <Stack spacing={1.5}>
        <Typography variant="body2" color="text.secondary">다음 TBM부터 적용됩니다. 지난 기록은 바뀌지 않습니다.</Typography>
        {items.map((item,i) => <Stack key={item.key} direction="row" alignItems="center" spacing={1}>
          <TextField size="small" multiline fullWidth label={`기본 항목 ${i+1}`} value={item.title} disabled={mutation.isPending}
            error={[...item.title.trim()].length>200}
            helperText={[...item.title.trim()].length>200 ? "기본 항목은 200자 이내로 다듬어주세요." : undefined}
            onChange={e=>setItems(items.map(row=>row.key===item.key?{...row,title:e.target.value}:row))} />
          <ActionIcon label={`기본 항목 ${i+1} 삭제`} disabled={mutation.isPending} onClick={()=>setItems(items.filter(row=>row.key!==item.key))}><DeleteOutlineIcon/></ActionIcon>
        </Stack>)}
        <Stack direction="row" flexWrap="wrap" gap={1}>
          <Button disabled={mutation.isPending || items.length>=500} startIcon={<AddIcon/>} onClick={()=>setItems([...items,{key:'n'+(++serial.current),title:''}])}>항목 추가</Button>
          <Button variant="outlined" size="small" disabled={mutation.isPending || !date} startIcon={<PlaylistAddIcon/>} onClick={()=>setImporting(true)}>위험성평가에서 가져오기</Button>
        </Stack>
        {!items.length && <Typography variant="body2">저장하면 새 TBM은 빈 체크리스트로 시작합니다.</Typography>}
        {duplicates && <Alert severity="warning">중복된 문구를 확인해주세요.</Alert>}
        {mutation.isError && <Alert severity="error">{mutation.error?.response?.data?.message || "기본 목록을 저장하지 못했습니다."}</Alert>}
      </Stack>
    </DialogContent>
    <DialogActions><Button disabled={mutation.isPending} onClick={dismiss}>취소</Button>
      <Button variant="contained" disabled={mutation.isPending || duplicates || titles.some(t=>!t || [...t].length>200)} onClick={()=>mutation.mutate()}>기본 목록 저장</Button>
    </DialogActions>
    {importing && <RiskPicker uniqueTitles api={api} scope={scope} date={date} items={items} onClose={()=>setImporting(false)}
      onAdd={rows=>{setItems([...items,...rows.map(row=>({...row,key:'n'+(++serial.current)}))]);setImporting(false);}}/>}
  </Dialog>;
}

function RiskPicker({ api, scope, date, items, onAdd, onClose, uniqueTitles=false }) {
  const [machine,setMachine] = useState("");
  const [selection,setSelection] = useState([]);
  const query = useQuery({ queryKey:["tbm-risk-items",...scope,date], queryFn:async ()=>(await api.get("/tbm/risk-items",{params:{date}})).data });
  const rows = query.data?.rows || [];
  const machines = new Map(rows.map(r=>[String(r.machine_id),r.machine_name]));
  const existing = new Set(items.filter(i=>i.source_eval_id).map(i=>Number(i.source_eval_id)));
  const existingText = new Set(items.map(i=>i.title.trim()));
  const alreadyAdded = row => existing.has(Number(row.eval_id)) || (uniqueTitles && existingText.has(riskText(row)));
  const visible = rows.filter(r=>String(r.machine_id)===machine);
  const picked = visible.filter(r=>selection.includes(r.eval_id) && !alreadyAdded(r));
  return <Dialog open onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="tbm-import-title">
    <DialogTitle id="tbm-import-title">위험성평가에서 가져오기</DialogTitle>
    <DialogContent dividers><Stack spacing={2}>
      {query.isLoading ? <Typography>평가 목록 조회 중…</Typography> : query.isError ? <Alert severity="error" action={<Button onClick={()=>query.refetch()}>다시 조회</Button>}>위험성평가를 불러오지 못했습니다.</Alert> :
        !rows.length ? <Alert severity="info">{date} 기준 평가 주기에 등록된 평가가 없습니다.</Alert> : <>
          <Typography variant="body2" color="text.secondary">실시일 {date} · {query.data.start} 시작 평가 주기</Typography>
          <TextField select fullWidth label="설비·구역" size="small" value={machine} onChange={e=>{setMachine(e.target.value);setSelection([]);}}>
            {[...machines].map(([id,name])=><MenuItem key={id} value={id}>{name}</MenuItem>)}
          </TextField>
          {visible.length>0 && <Box>{visible.map(r=>{
            const added = alreadyAdded(r);
            return <Box key={r.eval_id} component="label" sx={{display:"flex",alignItems:"center",gap:1,py:1,borderBottom:1,borderColor:"divider",cursor:added?"default":"pointer"}}>
              <Checkbox size="small" checked={added || selection.includes(r.eval_id)} disabled={added} onChange={(_,checked)=>setSelection(prev=>checked?[...prev,r.eval_id]:prev.filter(id=>id!==r.eval_id))}/>
              <Box sx={{minWidth:0,overflowWrap:"anywhere"}}>
                <Typography variant="caption" color="text.secondary">{r.process_name}{added?" · 이미 추가됨":""}</Typography>
                <Typography variant="body2">{r.hazard}</Typography>
                <Typography variant="body2" color="text.secondary">{r.improve_plan || "등록된 개선대책 없음"}</Typography>
              </Box></Box>;
          })}</Box>}
        </>}
    </Stack></DialogContent>
    <DialogActions><Button onClick={onClose}>취소</Button><Button variant="contained" disabled={!picked.length || picked.length+items.length>500}
      onClick={()=>onAdd(picked.map(r=>({template_id:null,source_eval_id:Number(r.eval_id),title:riskText(r),is_checked:false})))}>{picked.length}개 가져오기</Button></DialogActions>
  </Dialog>;
}

export function SessionChecklist({items,onChange,editable,api,scope,date}) {
  const [importing,setImporting] = useState(false);
  const [editing,setEditing] = useState(null);
  const update = (key,patch) => onChange(items.map(i=>rowKey(i)===key?{...i,...patch}:i));
  return <>
    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap" sx={{mb:1}}>
      <Typography sx={{fontWeight:800}}>체크리스트</Typography>
      {editable && <Button size="small" variant="outlined" startIcon={<PlaylistAddIcon/>} disabled={!date} onClick={()=>setImporting(true)}>위험성평가에서 가져오기</Button>}
    </Stack>
    <Divider/>
    {items.map((item,i)=>{
      const key=rowKey(item);
      return <Stack key={key} direction="row" alignItems="center" spacing={0.5} sx={{py:0.75,borderBottom:1,borderColor:"divider"}}>
        <Checkbox size="small" id={`tbm-check-${key}`} checked={item.is_checked} inputProps={{"aria-label":`항목 ${i+1} 확인`}} onChange={(_,checked)=>update(key,{is_checked:checked})}/>
        <Box sx={{flex:1,minWidth:0,overflowWrap:"anywhere"}}>
          {item.source_eval_id && <Typography display="block" variant="caption" color="text.secondary">위험성평가</Typography>}
          {editable && editing===key ? <TextField fullWidth autoFocus size="small" multiline label="이번 TBM 확인 문구" value={item.title}
            error={!item.title.trim()} onChange={e=>update(key,{title:limit(e.target.value,400)})}/> :
            <Typography variant="body2" component="label" htmlFor={`tbm-check-${key}`} sx={{cursor:"pointer"}}>{item.title}</Typography>}
        </Box>
        {editable && <>
          <ActionIcon label={editing===key?"문구 편집 완료":`항목 ${i+1} 문구 수정`} onClick={()=>setEditing(editing===key?null:key)}>{editing===key?<CheckIcon fontSize="small"/>:<EditOutlinedIcon fontSize="small"/>}</ActionIcon>
          <ActionIcon label={`이번 TBM에서 항목 ${i+1} 제외`} onClick={()=>onChange(items.filter(r=>rowKey(r)!==key))}><DeleteOutlineIcon fontSize="small"/></ActionIcon>
        </>}
      </Stack>;
    })}
    {!items.length && <Typography variant="body2" sx={{py:2}} color="text.secondary">등록된 체크 항목이 없습니다.</Typography>}
    {importing && <RiskPicker key={date} api={api} scope={scope} date={date} items={items} onClose={()=>setImporting(false)}
      onAdd={additions=>{onChange([...items,...additions]);setImporting(false);}}/>}
  </>;
}
